import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Snapshot } from '../types';
import { TransitionMode } from '../store/app';
import {
  FPS,
  TransitionPaths,
  calculateTransitionFrameCount,
  calculateObjectFrameCounts,
  generateTransitionDiffSnapshot,
  computeInterpolatedSnapshot,
  prepareTransitionPaths,
} from '../utils/animation';

export interface PlaybackEngineState {
  /** The snapshot to render (interpolated during transitions, raw otherwise) */
  currentSnapshot: Snapshot | undefined;
  /** Whether playback is actively running */
  isPlaying: boolean;
  /** Whether playback has reached the last snapshot */
  isFinished: boolean;
  /** Whether a transition animation is currently running */
  isTransitioning: boolean;
  /** The current snapshot index (target index during transitions) */
  currentIndex: number;
  /** Total number of snapshots */
  totalSnapshots: number;
}

export interface PlaybackEngineControls {
  play: () => void;
  pause: () => void;
  nextFrame: () => void;
  prevFrame: () => void;
  reset: () => void;
  goToFrame: (index: number) => void;
}

export type UsePlaybackEngineReturn = PlaybackEngineState & PlaybackEngineControls;

export function usePlaybackEngine(
  snapshots: Snapshot[],
  animationMode: TransitionMode,
): UsePlaybackEngineReturn {
  const [activeSnapshotIndex, setActiveSnapshotIndex] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isFinished, setIsFinished] = useState<boolean>(false);

  // Transition state
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const [currentTransitionFrame, setCurrentTransitionFrame] = useState<number>(0);
  const [totalTransitionFrames, setTotalTransitionFrames] = useState<number>(0);
  const [transitionDiffSnapshot, setTransitionDiffSnapshot] = useState<Snapshot | null>(null);
  const [transitionStartSnapshot, setTransitionStartSnapshot] = useState<Snapshot | null>(null);
  const [transitionEndSnapshot, setTransitionEndSnapshot] = useState<Snapshot | null>(null);
  const [transitionPaths, setTransitionPaths] = useState<TransitionPaths | null>(null);
  const [originSnapshotIndex, setOriginSnapshotIndex] = useState<number>(0);
  const [objectFrameCounts, setObjectFrameCounts] = useState<{
    ball: number;
    goalkeepers: number[];
    players: number[];
    opponents: number[];
  } | null>(null);

  // Keep a mutable ref to the current animationMode so the timer callback sees the latest value
  const animationModeRef = useRef<TransitionMode>(animationMode);
  useEffect(() => {
    animationModeRef.current = animationMode;
  }, [animationMode]);

  const transitionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const goToNextFrameRef = useRef<(() => void) | null>(null);

  const snapshotCount = snapshots.length;

  // Clear transition state helper
  const clearTransition = useCallback(() => {
    if (transitionTimer.current) {
      clearTimeout(transitionTimer.current);
      transitionTimer.current = null;
    }
    setIsTransitioning(false);
    setCurrentTransitionFrame(0);
    setTotalTransitionFrames(0);
    setTransitionDiffSnapshot(null);
    setTransitionStartSnapshot(null);
    setTransitionEndSnapshot(null);
    setTransitionPaths(null);
    setObjectFrameCounts(null);
  }, []);

  // Transition tick effect
  useEffect(() => {
    if (isTransitioning) {
      transitionTimer.current = setTimeout(() => {
        const nextFrame = currentTransitionFrame + 1;
        if (nextFrame > totalTransitionFrames) {
          clearTransition();
          if (isPlaying) {
            goToNextFrameRef.current?.();
          }
        } else {
          setCurrentTransitionFrame(nextFrame);
        }
      }, 1000 / FPS);
    }

    return () => {
      if (transitionTimer.current) {
        clearTimeout(transitionTimer.current);
      }
    };
  }, [isTransitioning, currentTransitionFrame, totalTransitionFrames, isPlaying, clearTransition]);

  // Start a transition between two snapshot indices
  const startTransition = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (snapshotCount < 2) return;

      const startSnap = snapshots[fromIndex];
      const endSnap = snapshots[toIndex];
      if (!startSnap || !endSnap) return;

      // Recorded paths describe the (i-1 -> i) transition and live on
      // snapshot i: playing forward they are on the target snapshot, stepping
      // backward they are on the origin snapshot and run in reverse.
      const reversed = toIndex < fromIndex;
      const paths = prepareTransitionPaths(
        startSnap,
        endSnap,
        reversed ? startSnap.paths : endSnap.paths,
        reversed,
      );

      const mode = animationModeRef.current;
      const frameCount = calculateTransitionFrameCount(
        startSnap,
        endSnap,
        mode,
        paths,
      );

      if (mode === TransitionMode.DISTANCE_BASED) {
        setObjectFrameCounts(
          calculateObjectFrameCounts(startSnap, endSnap, paths),
        );
      }
      setTransitionStartSnapshot(startSnap);
      setTransitionEndSnapshot(endSnap);
      setTransitionPaths(paths);

      setTransitionDiffSnapshot(
        generateTransitionDiffSnapshot(startSnap, endSnap, frameCount),
      );
      setOriginSnapshotIndex(fromIndex);
      setTotalTransitionFrames(frameCount);
      setCurrentTransitionFrame(0);
      setIsTransitioning(true);
      setActiveSnapshotIndex(toIndex);
    },
    [snapshots, snapshotCount],
  );

  // Go to next frame (snapshot transition)
  const nextFrame = useCallback(() => {
    clearTransition();

    const nextIndex = activeSnapshotIndex + 1;
    if (nextIndex >= snapshotCount) {
      // Reached the end
      setIsPlaying(false);
      setIsFinished(true);
      return;
    }

    startTransition(activeSnapshotIndex, nextIndex);
  }, [activeSnapshotIndex, snapshotCount, clearTransition, startTransition]);

  // Go to previous frame
  const prevFrame = useCallback(() => {
    clearTransition();

    const prevIndex = activeSnapshotIndex - 1;
    if (prevIndex < 0) {
      setIsPlaying(false);
      return;
    }

    startTransition(activeSnapshotIndex, prevIndex);
  }, [activeSnapshotIndex, clearTransition, startTransition]);

  // Update the ref whenever nextFrame changes
  useEffect(() => {
    goToNextFrameRef.current = nextFrame;
  }, [nextFrame]);

  // Play
  const play = useCallback(() => {
    if (isFinished) {
      // If finished, reset first then play
      setActiveSnapshotIndex(0);
      setIsFinished(false);
    }
    setIsPlaying(true);
    setIsFinished(false);
    // Trigger the first next frame. We need to do it in a microtask
    // because if isFinished was true, the state update to index 0 hasn't happened yet.
    setTimeout(() => {
      goToNextFrameRef.current?.();
    }, 0);
  }, [isFinished]);

  // Pause
  const pause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // Reset
  const reset = useCallback(() => {
    clearTransition();
    setIsPlaying(false);
    setIsFinished(false);
    setActiveSnapshotIndex(0);
  }, [clearTransition]);

  // Go to specific frame
  const goToFrame = useCallback(
    (index: number) => {
      if (index < 0 || index >= snapshotCount) return;
      clearTransition();
      setIsPlaying(false);
      setIsFinished(false);
      setActiveSnapshotIndex(index);
    },
    [snapshotCount, clearTransition],
  );

  // Compute the current snapshot to render
  const currentSnapshot = useMemo((): Snapshot | undefined => {
    if (snapshotCount === 0) return undefined;

    if (isTransitioning && transitionDiffSnapshot) {
      const originSnap = snapshots[originSnapshotIndex];
      if (!originSnap) return snapshots[activeSnapshotIndex];

      return computeInterpolatedSnapshot(
        originSnap,
        transitionStartSnapshot || originSnap,
        transitionEndSnapshot || snapshots[activeSnapshotIndex],
        transitionDiffSnapshot,
        currentTransitionFrame,
        animationModeRef.current,
        objectFrameCounts,
        transitionPaths,
        totalTransitionFrames,
      );
    }

    return snapshots[activeSnapshotIndex];
  }, [
    snapshots,
    snapshotCount,
    activeSnapshotIndex,
    isTransitioning,
    transitionDiffSnapshot,
    originSnapshotIndex,
    currentTransitionFrame,
    transitionStartSnapshot,
    transitionEndSnapshot,
    transitionPaths,
    totalTransitionFrames,
    objectFrameCounts,
  ]);

  return {
    currentSnapshot,
    isPlaying,
    isFinished,
    isTransitioning,
    currentIndex: activeSnapshotIndex,
    totalSnapshots: snapshotCount,
    play,
    pause,
    nextFrame,
    prevFrame,
    reset,
    goToFrame,
  };
}
